import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { getManagerTeamMemberResponsesAPI, getManagerCycleDetailAPI } from "api/apiHelper";
import { AppraisalResponse, AvailableCycle } from "types/appraisal-response-types";
import Card from "components/Card";
import Backdrop from "components/Backdrop";
import ErrorBox from "components/ErrorBox";
import Button from "components/Button";
import { toast } from "react-toastify";
import { MdArrowBack, MdCheckCircle, MdPending } from "react-icons/md";

const ViewTeamMemberResponses = () => {
  const { cycleId, employeeId } = useParams<{ cycleId: string; employeeId: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [cycle, setCycle] = useState<AvailableCycle | null>(null);
  const [responses, setResponses] = useState<AppraisalResponse[]>([]);

  useEffect(() => {
    if (cycleId && employeeId) {
      fetchData();
    }
  }, [cycleId, employeeId]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const [cycleResponse, responsesResponse] = await Promise.all([
        getManagerCycleDetailAPI(Number(cycleId)),
        getManagerTeamMemberResponsesAPI(Number(cycleId), Number(employeeId))
      ]);
      
      setCycle(cycleResponse.data);
      setResponses(responsesResponse.data);
    } catch (err: any) {
      const errorMessage = err?.response?.data?.error || err.message || "Failed to load responses.";
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getReviewTypeLabel = (type: string) => {
    switch (type) {
      case 'SELF':
        return 'Self Review';
      case 'MANAGER':
        return 'Manager Review';
      case 'PEER':
        return 'Peer Review';
      case 'CLIENT':
        return 'Client Review';
      default:
        return type;
    }
  };

  const getReviewTypeColor = (type: string) => {
    switch (type) {
      case 'SELF':
        return 'bg-blue-100 text-blue-700 dark:bg-blue-900/20 dark:text-blue-400';
      case 'MANAGER':
        return 'bg-purple-100 text-purple-700 dark:bg-purple-900/20 dark:text-purple-400';
      case 'PEER':
        return 'bg-orange-100 text-orange-700 dark:bg-orange-900/20 dark:text-orange-400';
      case 'CLIENT':
        return 'bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-400';
      default:
        return 'bg-gray-100 text-gray-700 dark:bg-gray-900/20 dark:text-gray-400';
    }
  };

  if (loading) {
    return <Backdrop show={true} />;
  }

  if (error) {
    return (
      <div className="mt-5">
        <Card extra="p-6">
          <ErrorBox errorMessage={error} />
          <div className="mt-4 flex justify-end">
            <Button
              type="button"
              varient="secondary"
              onClick={() => navigate('/manager-appraisals')}
            >
              Back to Appraisals
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  const employee = cycle?.participants.find((p) => p.id === Number(employeeId));

  const groupedResponses = {
    SELF: responses.find(r => r.reviewer_type === 'SELF'),
    MANAGER: responses.find(r => r.reviewer_type === 'MANAGER'),
    PEER: responses.filter(r => r.reviewer_type === 'PEER'),
    CLIENT: responses.find(r => r.reviewer_type === 'CLIENT'),
  };

  return (
    <div className="mt-5">
      <div className="mb-6 flex items-center justify-between">
        <Button
          varient="secondary"
          onClick={() => navigate('/manager-appraisals')}
          className="flex items-center gap-2 px-4 py-2"
        >
          <MdArrowBack className="h-5 w-5" />
          Back
        </Button>
      </div>

      <Card extra="p-6">
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-navy-700 dark:text-white mb-2">
            {cycle?.name}
          </h2>
          {cycle?.description && (
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              {cycle.description}
            </p>
          )}
          
          <div className="flex items-center gap-4 mb-4">
            {employee?.photo ? (
              <img
                src={employee.photo}
                alt={employee.full_name || "Employee"}
                className="w-16 h-16 rounded-full object-cover border-2 border-brand-300 dark:border-brand-700"
              />
            ) : (
              <div className="w-16 h-16 rounded-full bg-brand-100 dark:bg-brand-900/20 flex items-center justify-center border-2 border-brand-300 dark:border-brand-700">
                <span className="text-brand-600 dark:text-brand-400 font-semibold text-xl">
                  {employee?.first_name?.[0] || 'E'}
                </span>
              </div>
            )}
            <div>
              <h3 className="text-lg font-semibold text-navy-700 dark:text-white">
                {employee?.full_name || `${employee?.first_name || ''} ${employee?.last_name || ''}`.trim() || 'Unknown'}
              </h3>
              {employee?.email && (
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {employee.email}
                </p>
              )}
              {employee?.designation && (
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {employee.designation}
                </p>
              )}
            </div>
          </div>
        </div>

        <div className="space-y-6">
          <div>
            <h3 className="text-lg font-semibold text-navy-700 dark:text-white mb-4">
              Self Review
            </h3>
            {groupedResponses.SELF ? (
              <Card extra="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className={`px-3 py-1 text-xs font-semibold rounded ${getReviewTypeColor('SELF')}`}>
                      SELF REVIEW
                    </span>
                    {groupedResponses.SELF.status === 'SUBMITTED' ? (
                      <div className="flex items-center gap-2 text-green-600 dark:text-green-400">
                        <MdCheckCircle className="h-5 w-5" />
                        <span className="text-sm font-medium">Submitted</span>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 text-orange-600 dark:text-orange-400">
                        <MdPending className="h-5 w-5" />
                        <span className="text-sm font-medium">Pending</span>
                      </div>
                    )}
                    {groupedResponses.SELF.submitted_at && (
                      <span className="text-sm text-gray-500 dark:text-gray-400">
                        {formatDate(groupedResponses.SELF.submitted_at)}
                      </span>
                    )}
                  </div>
                  {groupedResponses.SELF.status === 'SUBMITTED' && (
                    <Button
                      varient="secondary"
                      onClick={() => navigate(`/manager-appraisals/response/${groupedResponses.SELF.id}`)}
                    >
                      View Response
                    </Button>
                  )}
                </div>
              </Card>
            ) : (
              <Card extra="p-4">
                <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400">
                  <MdPending className="h-5 w-5" />
                  <span>Self review not submitted yet</span>
                </div>
              </Card>
            )}
          </div>

          <div>
            <h3 className="text-lg font-semibold text-navy-700 dark:text-white mb-4">
              Manager Review
            </h3>
            {groupedResponses.MANAGER ? (
              <Card extra="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className={`px-3 py-1 text-xs font-semibold rounded ${getReviewTypeColor('MANAGER')}`}>
                      MANAGER REVIEW
                    </span>
                    {groupedResponses.MANAGER.status === 'SUBMITTED' ? (
                      <div className="flex items-center gap-2 text-green-600 dark:text-green-400">
                        <MdCheckCircle className="h-5 w-5" />
                        <span className="text-sm font-medium">Submitted</span>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 text-orange-600 dark:text-orange-400">
                        <MdPending className="h-5 w-5" />
                        <span className="text-sm font-medium">Pending</span>
                      </div>
                    )}
                    {groupedResponses.MANAGER.submitted_at && (
                      <span className="text-sm text-gray-500 dark:text-gray-400">
                        {formatDate(groupedResponses.MANAGER.submitted_at)}
                      </span>
                    )}
                  </div>
                  <div className="flex gap-2">
                    {groupedResponses.MANAGER.status === 'SUBMITTED' && (
                      <Button
                        varient="secondary"
                        onClick={() => navigate(`/manager-appraisals/response/${groupedResponses.MANAGER.id}`)}
                      >
                        View Response
                      </Button>
                    )}
                    <Button
                      varient="primary"
                      onClick={() => navigate(`/manager-appraisals/${cycleId}/employee/${employeeId}`)}
                    >
                      {groupedResponses.MANAGER.status === 'SUBMITTED' ? 'Edit Response' : 'Fill Review'}
                    </Button>
                  </div>
                </div>
              </Card>
            ) : (
              <Card extra="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400">
                    <MdPending className="h-5 w-5" />
                    <span>Manager review not started</span>
                  </div>
                  <Button
                    varient="primary"
                    onClick={() => navigate(`/manager-appraisals/${cycleId}/employee/${employeeId}`)}
                  >
                    Fill Review
                  </Button>
                </div>
              </Card>
            )}
          </div>

          <div>
            <h3 className="text-lg font-semibold text-navy-700 dark:text-white mb-4">
              Peer Reviews ({groupedResponses.PEER.length})
            </h3>
            {groupedResponses.PEER.length > 0 ? (
              <div className="space-y-3">
                {groupedResponses.PEER.map((response) => (
                  <Card key={response.id} extra="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <span className={`px-3 py-1 text-xs font-semibold rounded ${getReviewTypeColor('PEER')}`}>
                          PEER REVIEW
                        </span>
                        <span className="text-sm text-navy-700 dark:text-white">
                          Reviewer: {response.reviewer_name || 'Unknown'}
                        </span>
                        {response.status === 'SUBMITTED' ? (
                          <div className="flex items-center gap-2 text-green-600 dark:text-green-400">
                            <MdCheckCircle className="h-5 w-5" />
                            <span className="text-sm font-medium">Submitted</span>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2 text-orange-600 dark:text-orange-400">
                            <MdPending className="h-5 w-5" />
                            <span className="text-sm font-medium">Pending</span>
                          </div>
                        )}
                        {response.submitted_at && (
                          <span className="text-sm text-gray-500 dark:text-gray-400">
                            {formatDate(response.submitted_at)}
                          </span>
                        )}
                      </div>
                      {response.status === 'SUBMITTED' && (
                        <Button
                          varient="secondary"
                          onClick={() => navigate(`/manager-appraisals/response/${response.id}`)}
                        >
                          View Response
                        </Button>
                      )}
                    </div>
                  </Card>
                ))}
              </div>
            ) : (
              <Card extra="p-4">
                <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400">
                  <MdPending className="h-5 w-5" />
                  <span>No peer reviews yet</span>
                </div>
              </Card>
            )}
          </div>

          <div>
            <h3 className="text-lg font-semibold text-navy-700 dark:text-white mb-4">
              Client Review
            </h3>
            {groupedResponses.CLIENT ? (
              <Card extra="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className={`px-3 py-1 text-xs font-semibold rounded ${getReviewTypeColor('CLIENT')}`}>
                      CLIENT REVIEW
                    </span>
                    {groupedResponses.CLIENT.status === 'SUBMITTED' ? (
                      <div className="flex items-center gap-2 text-green-600 dark:text-green-400">
                        <MdCheckCircle className="h-5 w-5" />
                        <span className="text-sm font-medium">Submitted</span>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 text-orange-600 dark:text-orange-400">
                        <MdPending className="h-5 w-5" />
                        <span className="text-sm font-medium">Pending</span>
                      </div>
                    )}
                    {groupedResponses.CLIENT.submitted_at && (
                      <span className="text-sm text-gray-500 dark:text-gray-400">
                        {formatDate(groupedResponses.CLIENT.submitted_at)}
                      </span>
                    )}
                  </div>
                  {groupedResponses.CLIENT.status === 'SUBMITTED' && (
                    <Button
                      varient="secondary"
                      onClick={() => navigate(`/manager-appraisals/response/${groupedResponses.CLIENT.id}`)}
                    >
                      View Response
                    </Button>
                  )}
                </div>
              </Card>
            ) : (
              <Card extra="p-4">
                <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400">
                  <MdPending className="h-5 w-5" />
                  <span>Client review not submitted yet</span>
                </div>
              </Card>
            )}
          </div>
        </div>
      </Card>
    </div>
  );
};

export default ViewTeamMemberResponses;
